import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { apiConfig } from "../config/apiConfig";
import { filterFranchiseData, getBrandDetailsFromBrandJson } from "./share";

/**
 * Component types for nearby locations search
 */
export enum ComponentType {
    SHORT_FORM = 'shortform',
    OS_FLOW = 'osflow'
}

/**
 * Brand flag configuration mapping for different component types
 */
interface BrandFlagConfig {
    limitFlag: string;
    unitFlag: string;
}

/**
 * NearbyLocationsMiles - New implementation using miles-based API endpoint
 * This class fetchs nearby franchise locations using the new FranchiseGroup API endpoint
 */
export class NearbyLocationsMiles {
    private readonly zipcode: string;
    private readonly serviceId?: string;
    private readonly componentType: ComponentType;
    private readonly conceptCode: string;
    
    // Brand flag mapping based on component type
    private static readonly BRAND_FLAG_MAP: Record<ComponentType, BrandFlagConfig> = {
        [ComponentType.SHORT_FORM]: {
            limitFlag: 'nearest_franchiselimit_shortform',
            unitFlag: 'area_measure_values_shortform'
        },
        [ComponentType.OS_FLOW]: {
            limitFlag: 'nearest_franchiselimit_OSflow',
            unitFlag: 'area_measure_values_OSflow'
        }
    };

    constructor(
        zipcode: string,
        conceptCode: string,
        componentType: ComponentType = ComponentType.SHORT_FORM,
        serviceId?: string
    ) {
        this.zipcode = zipcode;
        this.conceptCode = conceptCode;
        this.componentType = componentType;
        this.serviceId = serviceId;
    }

    /**
     * Get brand configuration flags based on component type
     */
    private getBrandFlagConfig(): BrandFlagConfig {
        return NearbyLocationsMiles.BRAND_FLAG_MAP[this.componentType];
    }

    /**
     * Convert miles to kilometers
     */
    private convertToKilometers(miles: number): number {
        const KM_CONVERSION_FACTOR = 1.60934;
        return Math.round(miles * KM_CONVERSION_FACTOR);
    }

    /**
     * Determine unit type from brand data
     */
    private getUnitType(brandData: any): string {
        const flagConfig = this.getBrandFlagConfig();
        const areaMeasureValue = brandData?.[flagConfig.unitFlag];
        return areaMeasureValue?.includes('miles') ? 'miles' : 'km';
    }

    /**
     * Get franchise limit from brand data
     */
    private getFranchiseLimit(brandData: any): number {
        const flagConfig = this.getBrandFlagConfig();
        return Number(brandData?.[flagConfig.limitFlag]) || 50;
    }

    /**
     * Build API URL for the new miles endpoint
     */
    private buildApiUrl(limit: number): string {
        let api = `${apiConfig.WebLocationsFranchiseGroupAPI}/${this.zipcode.trim()}/miles/${limit}?apikey=${process.env.JS_API_KEY}`;
        
        if (this.serviceId) {
            api += `&serviceId=${this.serviceId}`;
            sessionStorage.removeItem('serviceIdValue');
        }
        
        return api;
    }

    /**
     * Search for nearby franchise locations using the new miles endpoint
     * @returns Promise with filtered franchise data or null on error
     */
    async searchNearbyLocations(): Promise<any> {
        try {
            const brandData = await getBrandDetailsFromBrandJson(this.conceptCode);
            
            // Get limit and unit from brand data
            let limit = this.getFranchiseLimit(brandData);
            const unit = this.getUnitType(brandData);
            
            if (unit === 'km') {
                limit = this.convertToKilometers(limit);
            }
            
            const apiUrl = this.buildApiUrl(limit);
            
            const data = await cachedAPIWrapper(apiUrl);
            
            return filterFranchiseData(data);
        } catch (error) {
            console.error('Error searching nearby locations with miles endpoint:', error);
            return null;
        }
    }
}
